import Phaser from 'phaser';
import { useClockStore } from '@/state/clockStore';
import type { ClockPhase } from '@/network/messages';

interface PhaseTint {
  color: number; // 0xRRGGBB
  alpha: number; // 0..1
}

const TINTS: Record<ClockPhase, PhaseTint> = {
  dawn:  { color: 0xffd9a8, alpha: 0.22 },
  day:   { color: 0xffffff, alpha: 0.0 },
  dusk:  { color: 0xff9a55, alpha: 0.30 },
  night: { color: 0x1a2a55, alpha: 0.55 },
};

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

function lerpColor(c1: number, c2: number, t: number): number {
  const r1 = (c1 >> 16) & 0xff, g1 = (c1 >> 8) & 0xff, b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff, g2 = (c2 >> 8) & 0xff, b2 = c2 & 0xff;
  const r = Math.round(lerp(r1, r2, t));
  const g = Math.round(lerp(g1, g2, t));
  const b = Math.round(lerp(b1, b2, t));
  return (r << 16) | (g << 8) | b;
}

function tintForTime(hour: number, minute: number): PhaseTint {
  const t = hour + minute / 60;
  // Smooth segments: night(20-5) → dawn(5-7) → day(7-18) → dusk(18-20) → night
  if (t >= 5 && t < 7) {
    const k = (t - 5) / 2; // 0..1 dawn ramp
    // night → dawn peak at 6, then dawn → day by 7
    if (k < 0.5) {
      const u = k / 0.5;
      return { color: lerpColor(TINTS.night.color, TINTS.dawn.color, u),
               alpha: lerp(TINTS.night.alpha, TINTS.dawn.alpha, u) };
    } else {
      const u = (k - 0.5) / 0.5;
      return { color: lerpColor(TINTS.dawn.color, TINTS.day.color, u),
               alpha: lerp(TINTS.dawn.alpha, TINTS.day.alpha, u) };
    }
  }
  if (t >= 7 && t < 18) return TINTS.day;
  if (t >= 18 && t < 20) {
    const k = (t - 18) / 2;
    if (k < 0.5) {
      const u = k / 0.5;
      return { color: lerpColor(TINTS.day.color, TINTS.dusk.color, u),
               alpha: lerp(TINTS.day.alpha, TINTS.dusk.alpha, u) };
    } else {
      const u = (k - 0.5) / 0.5;
      return { color: lerpColor(TINTS.dusk.color, TINTS.night.color, u),
               alpha: lerp(TINTS.dusk.alpha, TINTS.night.alpha, u) };
    }
  }
  return TINTS.night;
}

export class AmbientLightSystem {
  private scene: Phaser.Scene;
  private overlay: Phaser.GameObjects.Rectangle;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const cam = scene.cameras.main;
    this.overlay = scene.add
      .rectangle(0, 0, cam.width, cam.height, 0x000000, 0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(9000)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
    scene.scale.on('resize', this.onResize, this);
  }

  private onResize(size: Phaser.Structs.Size): void {
    this.overlay.setSize(size.width, size.height);
  }

  update(): void {
    const now = useClockStore.getState().getNow();
    if (!now) {
      this.overlay.setFillStyle(0x000000, 0);
      return;
    }
    const t = tintForTime(now.hour, now.minute);
    this.overlay.setFillStyle(t.color, t.alpha);
  }

  destroy(): void {
    this.scene.scale.off('resize', this.onResize, this);
    this.overlay.destroy();
  }
}
